import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

/**
 * shadcn/ui Button, in the AeroVect palette.
 *
 * Two deliberate departures from the generated version: the variants are named
 * for what this app already calls them (primary / ghost / quiet), and every
 * variant states its own `border` and `bg` because Tailwind's preflight is
 * disabled here — a pasted shadcn button would otherwise inherit the browser's
 * default button chrome. See tailwind.config.js.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full font-sans font-semibold m-0 cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-default disabled:opacity-55",
  {
    variants: {
      variant: {
        primary: "border border-blue bg-blue text-white hover:bg-blueElectric",
        ghost: "border border-line bg-white text-ink hover:bg-offWhite",
        quiet:
          "border-0 bg-transparent text-slate hover:text-blue px-0 py-0 h-auto",
        destructive:
          "border border-groundedDot bg-groundedDot text-white hover:bg-groundedText",
      },
      size: {
        sm: "h-8 px-3 text-[12.5px]",
        md: "h-9 px-4 text-[13px]",
        lg: "h-10 px-5 text-[13.5px]",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, type, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        // Inside the config form every button is an action, never a submit —
        // a stray submit would reload the page and drop the draft.
        type={asChild ? undefined : (type ?? "button")}
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
